import React, { FC } from 'react';
import { RootState, useSelector } from 'src/store';
import { changeEnterHTML } from 'src/utils/changeEnter';
import { getFontURL } from 'src/utils/getFontUrl';
import { trackingList } from 'src/utils/valueHTML';

const CardModalHTML: FC = () => {
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const radius = useSelector((state: RootState) => state.imageUpload.radius);
  const deeplink = useSelector(
    (state: RootState) => state.imageUpload.deeplink
  );
  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const btnAgain = useSelector((state: RootState) => state.buttons.btnAgain);
  const tracking = useSelector((state: RootState) => state.trackings.tracking);
  const button1 = useSelector((state: RootState) => state.trackings.button1);
  const button2 = useSelector((state: RootState) => state.trackings.button2);

  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.7;

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(btnFontFamily?.button);
  const closeBtnFontURL = getFontURL(btnFontFamily?.close_button);

  const style = `
    @font-face {
      font-family: 'title';
      src: url(${titleFontURL});
    }
    @font-face {
      font-family: 'subTitle';
      src: url(${subTitleFontURL});
    }
    @font-face {
      font-family: 'btn';
      src: url(${btnFontURL});
    }
    @font-face {
      font-family: 'closeBtn';
      src: url(${closeBtnFontURL});
    }

     * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      list-style: none;
      text-decoration: none;      
    }
    
    html,
    body {
      height: 100%;
      overflow: hidden;
      font-size: 16px;      
    }
    main {
      height: 100%;
      position: relative;
    }
    a {
      color: #000000;
      cursor: pointer;
    }
    button {
      background: none;
      border: none;
    }

    .root-view {
      width: 100%;
      height: 100%;
      position: relative;
      background: none;
    }

    .background {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      background: rgba(0, 0, 0, ${displayValue});
    }

    .modal-wrapper {
      width: 314px;
      height: auto;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 32px 20px 20px 20px;
      border-radius: ${radius?.background}px;
      background:${bgColor?.background};
    }

    .content-wrapper {
      width: 100%;
      height: auto;
      display: flex;
      flex-direction: column;
      background-color: transparent;
      margin-bottom: 24px;
      gap: 10px;
    }

    .content-wrapper .title {
      width: 100%;
      min-height: 48px;
      height: auto;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .content-wrapper .title p {
      width: 100%;
      font-size: ${fontSize?.title}rem;
      font-weight: ${fontWeight?.title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily?.title
          : 'title'
      };
      overflow: hidden;
      text-align: ${textAlign?.title};
      color: ${color?.title};
    }

    .content-wrapper .sub-title {
      width: 100%;
      min-height: 56px;
      height: auto;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .content-wrapper .sub-title p {
      width: 100%;
      font-size: ${fontSize?.sub_title}rem;
      font-weight: ${fontWeight?.sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily?.sub_title
          : 'subTitle'
      };
      text-align: ${textAlign?.sub_title};
      line-height: 1.4;
      color: ${color?.sub_title};
    }

    .btn-wrapper {
      width: 100%;
      height: 48px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 8px;
    }

    .btn-close {
      width: 50%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      background-color: rgba(${btnBgColorR?.close_button}, ${
    btnBgColorG?.close_button
  }, ${btnBgColorB?.close_button}, ${btnBgColorA?.close_button});
      border-radius: ${radius?.close_button}px;
    }

    .btn-close p {
      font-size: ${btnFontSize?.close_button}rem;
      font-weight: ${btnFontWeight?.close_button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.close_button
        )
          ? btnFontFamily?.close_button
          : 'closeBtn'
      };
      color: ${btnColor?.close_button};
    }

    .btn-inner {
      width: 50%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      background-color: rgba(${btnBgColorR?.button}, ${btnBgColorG?.button}, ${
    btnBgColorB?.button
  }, ${btnBgColorA?.button});
      border-radius: ${radius?.button}px;
    }

    .btn-inner p {
      font-size: ${btnFontSize?.button}rem;
      font-weight: ${btnFontWeight?.button};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.button
        )
          ? btnFontFamily?.button
          : 'btn'
      };
      color: ${btnColor?.button};
    }

    @media screen and (min-width: 355px) {
      .modal-wrapper {
        width: 314px;
      }
    }`;

  const deeplink_list = JSON.stringify(deeplink?.background);

  const html = `
    <div class="root">
			<div class="background" onclick="appboyBridge.logClick('background'); appboyBridge.closeMessage();"></div>
			<div class="modal-wrapper">
				<div class="content-wrapper">
					<div class="title">
						<p>${changeEnterHTML(text?.title)}</p>
					</div>
					<div class="sub-title">
						<p>
						    ${changeEnterHTML(text?.sub_title)}
						</p>
					</div>
				</div>
				<div class="btn-wrapper">
					<div class="btn-close"><p>${btnText?.close_button}</p></div>
					<div class="btn-inner"><p>${btnText?.button}</p></div>
				</div>
			</div>
			<div id="deeplink" style="display: none">${deeplink_list}</div>
		</div>`;

  const tracking_list = JSON.stringify([
    tracking?.close_button?.[0],
    tracking?.button?.[0]
  ]);

  const tracking_button_list = trackingList('CARD MODAL', button1, button2);

  const js = `
    document.addEventListener("DOMContentLoaded", function(){

      let content = document.getElementById("deeplink").textContent;     
      let link = content.replace(/\\n|\\t/g, "").trim();
      let deeplink = JSON.parse(link);
    
      let tracking = ${tracking_list};            
      let button1 = ${tracking_button_list[0]};
      let button2 = ${tracking_button_list[1]};

      document.querySelector(".btn-close").addEventListener("click", function () {
        try {
          appboyBridge.logClick(tracking[0]);
          if (button1[0].indexOf(0) !== -1) {
            appboyBridge.logClick("0");
          }
          if (button2[0].indexOf(0) !== -1) {
            appboyBridge.logClick("1");
          }
          if ("${btnAgain?.type}" === "attribute") {
            appboyBridge.getUser().setCustomUserAttribute("${btnAgain?.close_button}", new Date());
          }
          if ("${btnAgain?.type}" === "event") {
            appboyBridge.logCustomEvent("${btnAgain?.close_button}");
          }
        } catch (err) {}
        appboyBridge.closeMessage();
      });

      document.querySelector(".btn-inner").addEventListener("click", function () {
        try {
          appboyBridge.logClick(tracking[1]);
          if (button1[1].indexOf(0) !== -1) {
            appboyBridge.logClick("0");
          }
          if (button2[1].indexOf(0) !== -1) {
            appboyBridge.logClick("1");
          }   
        } catch (err) {}
        window.location = deeplink;
      });
    });
  `;
  return (
    <>
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          httpEquiv="Cache-Control"
          content="no-cache, no-store, must-revalidate"
        />
        <meta httpEquiv="Expires" content="0" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          as="style"
          href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdn.jsdelivr.net/npm/normalize.css@8.0.1/normalize.css"
        />
        <style dangerouslySetInnerHTML={{ __html: style }} />
      </head>
      <body dangerouslySetInnerHTML={{ __html: html }} />
      <script dangerouslySetInnerHTML={{ __html: js }} />
    </>
  );
};

export default CardModalHTML;
